/* ll-mobile-app.js
   Behaviour for the phone app shell.

   The tab bar, its Shop sheet and both badges moved to assets/llmt-tabbar.js
   with the 2026 tab bar. The full-screen search sheet still lives here.

   Every lookup is guarded. This file runs on every page of the store including
   ones that render no tab bar at all, so it must be inert rather than throwing
   when its elements are absent.

   No dependencies, no build step, deferred from layout/theme.liquid. */

/* ── Sticky identity strip (product template) ─────────────────────────────
   Thumbnail, product name, watchlist bell. No price and no buy button: the
   section renders its own sticky purchase bar below this one carrying all
   three, and having both put two Add to basket buttons on screen at once,
   stacked one above the other, saying the same thing at different sizes.

   Reveal rule: once the product's primary control has left the viewport.
   WHICH control that is depends on the product. Normally it is the Add to
   cart. On a sold-out product the theme hides that (offsetParent null, zero
   box, the label already changed to Out of stock) and the page's own
   watchlist bell becomes the primary control, so the bell is what the strip
   follows instead.

   This used to refuse to appear at all when there was no usable Add to cart,
   which was inherited from when the strip WAS a buy bar: a buy bar with
   nothing to buy is worse than no bar. That reasoning stopped applying the
   moment the strip became a bell. Sold out is exactly when a shopper wants
   the watchlist, and it was the one case where the strip refused to show.
   */
(function () {
  'use strict';

  var bar = document.querySelector('[data-ll-buybar]');
  if (!bar) return;

  var form = document.getElementById('pdp-form');
  var realBtn = form ? form.querySelector('[data-atc]') : document.querySelector('[data-atc]');
  var pageWatch = document.querySelector('[data-pdp-watch]');

  /* Nothing to follow and nothing to offer. */
  if (!realBtn && !pageWatch) return;

  /* offsetParent is null for anything display:none or inside it, which is how
     the theme hides the purchase controls on a sold-out product. */
  var onPage = function (el) {
    return !!el && el.offsetParent !== null && el.getBoundingClientRect().height > 0;
  };

  /* Resolved on every pass rather than once at load, because a variant switch
     can move a product between in stock and sold out, and that changes which
     control the strip should be following. */
  var anchor = function () {
    if (onPage(realBtn)) return realBtn;
    if (onPage(pageWatch)) return pageWatch;
    return null;
  };

  var show = function () {
    bar.hidden = false;
    void bar.offsetHeight;
    bar.classList.add('is-up');
    bar.setAttribute('aria-hidden', 'false');
  };

  var hide = function () {
    bar.classList.remove('is-up');
    bar.setAttribute('aria-hidden', 'true');
  };

  /* Reveal when the real button leaves the viewport.

     IntersectionObserver was the first approach and it was wrong here. IO
     reports intersection against the nearest clipping ancestors as well as the
     viewport, and the product gallery wraps this button in an overflow context,
     so IO reported isIntersecting false while the button was plainly on screen.
     The bar therefore never dropped back down.

     A bounding-rect test against the viewport is what the rule actually means,
     and it does not care what any ancestor clips. Scroll listeners are passive
     and the work is one rect read, so this is cheap.                          */
  var update = function () {
    var el = anchor();
    /* Neither control is on the page: no bell to offer, so stay down. */
    if (!el) { hide(); return; }
    var r = el.getBoundingClientRect();
    var onScreen = r.bottom > 0 && r.top < window.innerHeight;
    if (onScreen) { hide(); } else { show(); }
  };

  update();
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });

  /* A variant switch rewrites the Add to cart in place, which is what flips a
     product between the two anchors. Without this the strip would keep
     following the old one until the next scroll. */
  if (window.MutationObserver && realBtn) {
    new MutationObserver(update).observe(realBtn, {
      attributes: true, childList: true, subtree: true,
      attributeFilter: ['disabled', 'class', 'hidden', 'style']
    });
  }

  /* ── Watch toggle ───────────────────────────────────────────────────────
     Same principle as the basket button: drive the page control rather than
     write to localStorage from here. The product page already owns the
     ll_watchlist contract, the per-customer mirror, the price snapshot and the
     ll:watchlist:change dispatch. A second writer here would be a second
     source of truth for the same key, and they would drift.

     There are two [data-pdp-watch] controls on the page and they stay in sync
     with each other, so clicking the first is enough.                        */
  var watchBtn = bar.querySelector('[data-llbb-watch]');

  if (watchBtn && pageWatch) {
    watchBtn.hidden = false;

    var syncWatch = function () {
      var on = pageWatch.getAttribute('aria-pressed') === 'true';
      watchBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
      watchBtn.setAttribute('aria-label', on ? 'Remove from watchlist' : 'Watch this product for alerts');
    };

    watchBtn.addEventListener('click', function (e) {
      e.preventDefault();
      pageWatch.click();
      /* The page handler flips aria-pressed synchronously, but mirror on the
         event too so this stays right if that ever becomes async. */
      syncWatch();
    });

    syncWatch();
    document.addEventListener('ll:watchlist:change', syncWatch);
  }

}());

/* ── Bottom stack offsets (phones) ───────────────────────────────────────────
   Three fixed elements share the bottom of a product page: the tab bar, the
   section's own sticky purchase bar, and the identity strip above it. Each
   was positioned independently of the other two, so they collided. Measured
   on a 375px screen: the strip's lower edge sat 10px inside the purchase bar,
   and the purchase bar, at z-index 2147483000, painted straight across the
   whole tab bar. A hit test at the Home tab returned the purchase bar, so the
   mobile navigation was unreachable on every product page.

   The heights cannot be hardcoded here. The purchase bar changes height with
   what it holds (Add to basket, Pre-Order Now, or a Discord alerts link), and
   both change with the safe area. So each bar publishes the height it
   actually measures, and the CSS stacks on those.

   offsetHeight is the laid-out height and ignores the transform each bar uses
   to slide in and out, so a bar that is currently down still measures true.
   */
(function () {
  'use strict';

  var root = document.documentElement;
  var pdpBar = document.querySelector('.ll-pdp__bar');
  var buyBar = document.querySelector('[data-ll-buybar]');
  if (!pdpBar && !buyBar) return;

  var measure = function () {
    var pdpOn = !!pdpBar && pdpBar.getAttribute('data-on') === 'true';
    var buyUp = !!buyBar && buyBar.classList.contains('is-up');
    root.style.setProperty('--ll-pdpbar-h', pdpOn ? Math.round(pdpBar.offsetHeight) + 'px' : '0px');
    root.style.setProperty('--ll-buybar-h', buyUp ? Math.round(buyBar.offsetHeight) + 'px' : '0px');
  };

  measure();
  window.addEventListener('resize', measure, { passive: true });

  /* Both bars are toggled by an attribute or a class, so watching those is
     enough. Deliberately NOT on scroll: this reads offsetHeight, and forcing
     layout on every scroll frame to re-learn a height that has not changed is
     exactly the cost these bars exist to avoid. */
  if (window.MutationObserver) {
    var mo = new MutationObserver(measure);
    if (pdpBar) mo.observe(pdpBar, { attributes: true, attributeFilter: ['data-on'] });
    if (buyBar) mo.observe(buyBar, { attributes: true, attributeFilter: ['class', 'hidden'] });
  }
}());

/* The welcome section's story fold now lives in assets/ll-welcome.js (28 Sep 2026). */

/* ── Full-screen search (phones) ─────────────────────────────────────────────
   Bound to Shopify's own predictive search, which is what the search page
   matches on, so the sheet and the results it opens agree.

     /search/suggest.json?q=<query>&resources[type]=product&...

   It used to call XCloud's index at app.cloudsearchapp.com/api/v1/search. That
   was the right call while XCloud was the store's search; it stopped being so
   once the search page moved onto Shopify matching with our own filters, and
   it left the phone's main search entry point depending on an app nothing else
   needed. It also failed silently: the render ran only on a 2xx, so losing the
   app would have given shoppers a search box that typed and returned nothing.

   Response fields used: resources.results.products[] of
   {title, url, image, featured_image, price, price_min, price_max}.

   NO RESULT COUNT. XCloud returned numFoundProducts and this showed it.
   Predictive search does not report a total, and the only honest way to get one
   is a second request per keystroke, so the count is gone rather than guessed.

   Rows are built with DOM methods and textContent, never innerHTML. Product
   names are merchant data and building markup from them would be an injection
   path for anything that ever ends up in a product title.

   The tab bar's Search entry stays a real link and this intercepts the tap, so
   with no JavaScript it navigates to the search page exactly as before.      */
(function () {
  'use strict';

  var sheet = document.getElementById('ll-search-sheet');
  if (!sheet) return;

  var input = sheet.querySelector('[data-llss-input]');
  var form = sheet.querySelector('[data-llss-form]');
  var clearBtn = sheet.querySelector('[data-llss-clear]');
  var recentWrap = sheet.querySelector('[data-llss-recent]');
  var recentList = sheet.querySelector('[data-llss-recent-list]');
  var browseWrap = sheet.querySelector('[data-llss-browse]');
  var resultsWrap = sheet.querySelector('[data-llss-results]');
  var resultsList = sheet.querySelector('[data-llss-results-list]');
  var resultsHead = sheet.querySelector('[data-llss-results-head]');
  var allLink = sheet.querySelector('[data-llss-all]');
  var emptyMsg = sheet.querySelector('[data-llss-empty]');
  if (!input || !form) return;

  var RECENT_KEY = 'll_recent_searches';
  var MIN_CHARS = 2;
  /* Predictive search caps at 10. Eight rows fill a phone sheet without the
     list becoming something to scroll rather than scan. */
  var LIMIT = 8;
  var lastFocus = null;
  var timer = null;
  var controller = null;

  var open = function () {
    lastFocus = document.activeElement;
    sheet.hidden = false;
    void sheet.offsetHeight;
    sheet.classList.add('is-open');
    document.body.classList.add('llss-open');
    paintRecent();
    /* iOS raises the keyboard only for a focus inside a user gesture. This runs
       inside the tab's own click handler, which is what makes the keyboard come
       up rather than needing a second tap on the field. */
    input.focus();
  };

  var close = function () {
    sheet.classList.remove('is-open');
    document.body.classList.remove('llss-open');
    sheet.hidden = true;
    if (controller) { try { controller.abort(); } catch (e) {} }
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  };

  var tabLink = document.querySelector('.llmt__tab[href*="/search"]');
  if (tabLink) {
    tabLink.addEventListener('click', function (e) { e.preventDefault(); open(); });
  }

  sheet.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-llss-close]')) close();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !sheet.hidden) close();
  });

  var readRecent = function () {
    try {
      var v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Object.prototype.toString.call(v) === '[object Array]' ? v : [];
    } catch (e) { return []; }
  };

  var pushRecent = function (q) {
    if (!q) return;
    try {
      var list = readRecent().filter(function (x) { return x !== q; });
      list.unshift(q);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 6)));
    } catch (e) {}
  };

  var paintRecent = function () {
    if (!recentWrap || !recentList) return;
    var list = readRecent();
    recentList.textContent = '';
    if (!list.length) { recentWrap.hidden = true; return; }
    recentWrap.hidden = false;
    list.forEach(function (q) {
      var li = document.createElement('li');
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'llss__chip';
      b.textContent = q;
      b.addEventListener('click', function () { input.value = q; run(q); });
      li.appendChild(b);
      recentList.appendChild(li);
    });
  };

  var wipe = sheet.querySelector('[data-llss-wipe]');
  if (wipe) {
    wipe.addEventListener('click', function () {
      try { localStorage.removeItem(RECENT_KEY); } catch (e) {}
      paintRecent();
    });
  }

  var money = function (n) {
    var cfg = (window.sq && window.sq.config) || {};
    var sym = cfg.currencySymbol || String.fromCharCode(163);
    var v = Number(n);
    return isNaN(v) ? '' : sym + v.toFixed(2);
  };

  /* Shopify CDN sizing. These draw at 46px, so 92 covers 2x without shipping a
     full-size product photo to a phone on every keystroke. */
  var thumb = function (src) {
    if (!src) return '';
    if (src.indexOf('cdn.shopify.com') === -1 || /[?&]width=/.test(src)) return src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=92';
  };

  var render = function (data, q) {
    var products = (data && data.resources && data.resources.results &&
                    data.resources.results.products) || [];
    resultsList.textContent = '';

    if (!products.length) {
      resultsWrap.hidden = true;
      if (allLink) allLink.hidden = true;
      emptyMsg.hidden = false;
      return;
    }

    emptyMsg.hidden = true;
    resultsWrap.hidden = false;

    products.forEach(function (p) {
      var a = document.createElement('a');
      a.className = 'llss__row';
      a.href = p.url || '/search?q=' + encodeURIComponent(q) + '&type=product&options%5Bunavailable_products%5D=last';

      var src = thumb(p.image || (p.featured_image && p.featured_image.url));
      if (src) {
        var img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 46;
        img.height = 46;
        a.appendChild(img);
      }

      var meta = document.createElement('div');
      meta.className = 'llss__row-meta';

      var t = document.createElement('span');
      t.className = 'llss__row-title';
      t.textContent = p.title || '';
      meta.appendChild(t);

      if (p.price !== undefined && p.price !== null) {
        var pr = document.createElement('span');
        pr.className = 'llss__row-price';
        /* price is the low end of the range. Saying a flat price for a product
           whose variants differ would be quoting one the shopper may not get. */
        var ranged = p.price_min !== undefined && p.price_max !== undefined &&
                     String(p.price_min) !== String(p.price_max);
        pr.textContent = (ranged ? 'From ' : '') + money(p.price);
        meta.appendChild(pr);
      }

      a.appendChild(meta);
      a.addEventListener('click', function () { pushRecent(q); });
      resultsList.appendChild(a);
    });

    /* Predictive search reports no total, so this says what it actually is:
       the top matches, and a way through to the full set. It used to print
       XCloud numFoundProducts, and inventing a number here to replace it would
       be worse than not showing one. */
    if (resultsHead) resultsHead.textContent = 'Top matches';
    if (allLink) {
      allLink.hidden = false;
      allLink.href = '/search?q=' + encodeURIComponent(q) + '&type=product&options%5Bunavailable_products%5D=last&options%5Bprefix%5D=last';
      allLink.textContent = 'See all results';
    }
  };

  var run = function (q) {
    if (controller) { try { controller.abort(); } catch (e) {} }

    if (!q || q.length < MIN_CHARS) {
      resultsWrap.hidden = true;
      emptyMsg.hidden = true;
      if (browseWrap) browseWrap.hidden = false;
      paintRecent();
      return;
    }

    if (browseWrap) browseWrap.hidden = true;
    if (recentWrap) recentWrap.hidden = true;

    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
              '&resources[type]=product&resources[limit]=' + LIMIT +
              '&resources[options][unavailable_products]=last' +
              '&resources[options][fields]=title,product_type,vendor,tag,variants.title';

    controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;

    fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: controller ? controller.signal : undefined
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) { if (data) render(data, q); })
      .catch(function (err) {
        /* An aborted request is the next keystroke arriving, not a failure.
           Anything else leaves whatever is already on screen. */
        if (err && err.name === 'AbortError') return;
      });
  };

  input.addEventListener('input', function () {
    var q = input.value.trim();
    if (clearBtn) clearBtn.hidden = q.length === 0;
    clearTimeout(timer);
    /* 220ms skips most intermediate keystrokes while still feeling like the
       results track typing. */
    timer = setTimeout(function () { run(q); }, 220);
  });

  if (clearBtn) {
    clearBtn.addEventListener('click', function () {
      input.value = '';
      clearBtn.hidden = true;
      run('');
      input.focus();
    });
  }

  form.addEventListener('submit', function () {
    var q = input.value.trim();
    pushRecent(q);
    /* The visible field is named llq so XCloud's overlay does not attach to it,
       so the real q parameter has to be filled in here or the search page gets
       none. That name can revert to q once the app is uninstalled. */
    var hidden = form.querySelector('[data-llss-q]');
    if (hidden) hidden.value = q;
  });
}());

/* ── Back to top (phones) ────────────────────────────────────────────────────
   Two jobs: show the button only once there is something to go back from, and
   guarantee the click scrolls.

   On the click handler: the theme's footer script binds to [data-ll-top], and
   nothing in the theme renders that attribute, so that listener has always been
   attached to nothing. The real control is [data-back-to-top]. Empire's bundle
   does handle it, but rather than depend on a minified third-party listener for
   something this small, this binds directly. Scrolling to 0 twice is harmless
   if both fire.

   Visibility is driven by a passive scroll listener rather than
   IntersectionObserver: the rule is about scroll distance, not about whether
   any particular element is in view, and IO already misled me once on this
   theme by reporting against a clipping ancestor rather than the viewport.   */
(function () {
  'use strict';

  var wrap = document.querySelector('[data-back-to-top]');
  if (!wrap) return;

  var btn = wrap.querySelector('button') || wrap;
  var phone = window.matchMedia('(max-width: 767px)');
  var SHOW_AFTER = 600;

  var update = function () {
    if (!phone.matches) { wrap.classList.remove('is-visible'); return; }
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    wrap.classList.toggle('is-visible', y > SHOW_AFTER);
  };

  btn.addEventListener('click', function (e) {
    e.preventDefault();
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try {
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    } catch (err) {
      /* older Safari does not take the options object */
      window.scrollTo(0, 0);
    }
  });

  update();
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  if (phone.addEventListener) { phone.addEventListener('change', update); }
  else if (phone.addListener) { phone.addListener(update); }
}());

/* ── Hub coaching tips, folded (phones) ──────────────────────────────────────
   The TCG hub opens with two coaching panels between its heading and the first
   product: "Quick tip: find your TCG fast" at 172px and "New: Watchlist" at
   140px. On a 375px screen that is 312px, over a third of the viewport, spent
   on instructions before a shopper reaches anything buyable.

   Both are worth keeping, and neither is worth reading before the shop, so on
   phones each folds to its title line and opens on a tap.

   The panels ship with the hidden attribute and the section reveals them on a
   first visit, so folding happens at load regardless of that state: folding
   something already hidden is harmless, and it means a tip revealed later
   appears folded rather than having to be caught as it appears.
   */
(function () {
  'use strict';

  var tips = document.querySelectorAll('.lltc__tip, .llpz__tip, .llpo__tip, .llwa__tip, .llc__tip, .llur__tip');
  if (!tips.length) return;

  var phone = window.matchMedia('(max-width: 767px)');
  var CHEV = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';

  var wire = function (tip) {
    if (tip.llFoldReady) return;
    tip.llFoldReady = true;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'll-tip-toggle';
    btn.innerHTML = CHEV;
    tip.appendChild(btn);

    var setFolded = function (on) {
      tip.classList.toggle('is-folded', on);
      btn.setAttribute('aria-expanded', on ? 'false' : 'true');
      btn.setAttribute('aria-label', on ? 'Show this tip' : 'Hide this tip');
    };

    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      setFolded(!tip.classList.contains('is-folded'));
    });

    /* A folded tip is a single line, so the whole row is the target. Guarded on
       the folded state, which is why this can never swallow a tap on Got it or
       View watchlist: those are not rendered while it is folded. */
    tip.addEventListener('click', function () {
      if (tip.classList.contains('is-folded')) setFolded(false);
    });

    tip.llSetFolded = setFolded;
  };

  var apply = function () {
    for (var i = 0; i < tips.length; i++) {
      wire(tips[i]);
      tips[i].llSetFolded(phone.matches);
    }
  };

  apply();

  /* Rotating to a tablet width should hand the full panel back. */
  if (phone.addEventListener) { phone.addEventListener('change', apply); }
  else if (phone.addListener) { phone.addListener(apply); }
}());

/* The TCG hub's phone stage switch now lives in assets/ll-pokemon-hub.js (28 Sep 2026). */

/* The plush hub's phone filter panel now lives in assets/ll-plush-hub.js (28 Sep 2026). */
